/**
 * useAutoSave — saves newsletter every 15 s when dirty.
 * Also exposes manual save and creates a version snapshot every 5 minutes.
 * After each successful save, silently captures a thumbnail of the canvas
 * and stores it as cover_image (throttled to at most once per 60 s).
 */
import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { useEditorStore } from '../stores/editorStore';
import { API_URL } from '../../../api/api';

const AUTO_SAVE_INTERVAL   = 15_000;   // 15 s
const VERSION_SNAP_INTERVAL = 5 * 60_000; // 5 min

const THUMB_THROTTLE_MS = 60_000; // capture thumbnail at most once per minute

export function useAutoSave(canvasRef) {
  const {
    newsletterId, title, sections, meta, status,
    dirty, saving,
    setSaving, setSaveError, markSaved,
  } = useEditorStore();

  const saveTimer       = useRef(null);
  const versionTimer    = useRef(null);
  const lastSaveRef     = useRef(null);
  const lastThumbRef    = useRef(0);

  const token = () => localStorage.getItem('token');

  // Fire-and-forget: capture canvas → upload PNG → patch cover_image
  const captureThumbnail = useCallback(async () => {
    if (!canvasRef?.current || !newsletterId) return;
    if (Date.now() - lastThumbRef.current < THUMB_THROTTLE_MS) return;
    lastThumbRef.current = Date.now();
    try {
      const dataUrl = await toPng(canvasRef.current, {
        pixelRatio: 0.6,
        cacheBust: true,
        skipFonts: true,
        style: { overflow: 'visible' },
      });
      // Convert dataURL → Blob → FormData
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, 'thumbnail.png');
      const r = await axios.post(
        `${API_URL}/api/newsletters/assets/upload`,
        form,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      // Patch only cover_image — don't re-trigger a full save
      await axios.put(
        `${API_URL}/api/newsletters/${newsletterId}`,
        { cover_image: r.data.url },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
    } catch { /* thumbnail capture is non-critical — fail silently */ }
  }, [canvasRef, newsletterId]);

  const save = useCallback(async ({ silent = false } = {}) => {
    if (!newsletterId || saving) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/newsletters/${newsletterId}`,
        { title, sections, meta, status },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      markSaved();
      lastSaveRef.current = Date.now();
      // Capture thumbnail in the background after a short delay
      // (delay lets React finish painting the latest content)
      setTimeout(() => captureThumbnail(), 400);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message);
      if (!silent) console.error('[auto-save] error:', err.message);
    }
  }, [newsletterId, title, sections, meta, status, saving, setSaving, setSaveError, markSaved, captureThumbnail]);

  const snapshotVersion = useCallback(async () => {
    if (!newsletterId) return;
    try {
      await axios.post(
        `${API_URL}/api/newsletters/${newsletterId}/versions`,
        { label: 'auto-snapshot' },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
    } catch { /* non-critical */ }
  }, [newsletterId]);

  // Auto-save interval
  useEffect(() => {
    if (saveTimer.current) clearInterval(saveTimer.current);
    saveTimer.current = setInterval(() => {
      if (dirty) save({ silent: true });
    }, AUTO_SAVE_INTERVAL);
    return () => clearInterval(saveTimer.current);
  }, [dirty, save]);

  // Auto-version interval
  useEffect(() => {
    if (versionTimer.current) clearInterval(versionTimer.current);
    versionTimer.current = setInterval(snapshotVersion, VERSION_SNAP_INTERVAL);
    return () => clearInterval(versionTimer.current);
  }, [snapshotVersion]);

  // Save on browser unload
  useEffect(() => {
    const onUnload = () => { if (dirty) save({ silent: true }); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty, save]);

  return { save };
}
